/**
 * The shared 30-day money-back guarantee: a crisp SVG seal (scales perfectly,
 * themes with CSS variables, localizes its ring text) plus copy.
 *
 *   variant="seal"    seal + title + text, used under pricing plans.
 *   variant="inline"  small shield line, used right under paid CTAs.
 */
import { ShieldCheck } from 'lucide-react'
import { useId } from 'react'
import { useTranslation } from '../../lib/i18n'

export function GuaranteeSeal({ size = 112 }: { size?: number }) {
  const { t } = useTranslation()
  const pathId = `guarantee-ring-${useId().replace(/:/gu, '')}`
  const ring = `${t('pricing.guaranteeSealRing')} • ${t('pricing.guaranteeSealRing')} • `
  return (
    <svg
      className="guarantee-seal"
      width={size}
      height={size}
      viewBox="0 0 120 120"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <path
          id={pathId}
          d="M60,60 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0"
        />
      </defs>
      <circle className="guarantee-seal-outer" cx="60" cy="60" r="58" />
      <circle className="guarantee-seal-ring" cx="60" cy="60" r="52" />
      <circle className="guarantee-seal-core" cx="60" cy="60" r="36" />
      <text className="guarantee-seal-ring-text">
        <textPath
          href={`#${pathId}`}
          startOffset="0"
          textLength="276"
          lengthAdjust="spacingAndGlyphs"
        >
          {ring}
        </textPath>
      </text>
      <text className="guarantee-seal-days" x="60" y="65" textAnchor="middle">
        30
      </text>
      <text
        className="guarantee-seal-unit"
        x="60"
        y="80"
        textAnchor="middle"
      >
        {t('pricing.guaranteeSealDays')}
      </text>
    </svg>
  )
}

export function MoneyBackGuarantee({
  variant = 'seal',
}: {
  variant?: 'seal' | 'inline'
}) {
  const { t } = useTranslation()
  if (variant === 'inline') {
    return (
      <p className="guarantee-inline">
        <ShieldCheck size={14} aria-hidden="true" />
        {t('pricing.guarantee')}
      </p>
    )
  }
  return (
    <aside className="guarantee-block" aria-labelledby="guarantee-title">
      <GuaranteeSeal />
      <div className="guarantee-copy">
        <h3 id="guarantee-title">{t('pricing.guaranteeTitle')}</h3>
        <p>{t('pricing.guaranteeText')}</p>
      </div>
    </aside>
  )
}
